import BulkUpdateDateInput from '../bulkUpdateFields/BulkUpdateDateInput';
import BulkUpdateNumberInput from '../bulkUpdateFields/BulkUpdateNumberInput';
import BulkUpdateSingleSelectInput from '../bulkUpdateFields/BulkUpdateSingleSelectInput';
import BulkUpdateStringInput from '../bulkUpdateFields/BulkUpdateStringInput';
import { useColumnContextAPI } from '../../context/GetAllColumnContext';
import { getResolvedOptions } from '../../utils/selectWarningUtils';
import { DropdownOption } from '../../types';

export type BulkAttributeDataType = 'date' | 'singleSelect' | 'string' | 'number';

export type BulkAttributeConfig = {
  id: number;
  attrGroupId: number;
  attrInternalName: string;
  attrDisplayNameen: string;
  attrDisplayNamede: string;
  attrDataType: string;
  attrMinValue?: number | null;
  attrMaxValue?: number | null;
  formOrderNo?: number;
  valueOptions?: DropdownOption[];
};

export type BulkAttributeValue = string | number | Date | null;

export type BulkAttributeState = {
  enabled: boolean;
  value: BulkAttributeValue;
  error: boolean;
};

export type BulkInputRendererProps = {
  attribute: BulkAttributeConfig;
  state: BulkAttributeState;
  onChange: (value: BulkAttributeValue) => void;
  onBlur?: () => void;
};

type BulkInputRenderContext = {
  attribute: BulkAttributeConfig;
  state: BulkAttributeState;
  onChange: (value: BulkAttributeValue) => void;
  onBlur?: () => void;
  customConfig?: {
    SetDropdownOptions?: (args: { field: string; options: DropdownOption[] }) => DropdownOption[];
  };
  allDropdowns?: Record<string, DropdownOption[]>;
};

export function resolveBulkDataType(
  attribute: BulkAttributeConfig,
): BulkAttributeDataType | 'unknown' {
  const rawType = (attribute.attrDataType || '').toLowerCase();

  if (rawType === 'date') return 'date';
  if (rawType === 'singleselect') return 'singleSelect';
  if (rawType === 'number') return 'number';
  if (rawType === 'string' || rawType === 'text') return 'string';

  return 'unknown';
}

function renderDateInput({ attribute, state, onChange, onBlur }: BulkInputRenderContext) {
  return (
    <BulkUpdateDateInput
      id={attribute.attrInternalName}
      value={state.value instanceof Date ? state.value : null}
      onChange={onChange}
      onBlur={onBlur}
      disabled={!state.enabled}
      hasError={state.error}
    />
  );
}

function renderSingleSelectInput({
  attribute,
  state,
  onChange,
  onBlur,
  customConfig,
  allDropdowns,
}: BulkInputRenderContext) {
  const selectedValue =
    typeof state.value === 'string' || typeof state.value === 'number' ? state.value : null;
  const resolvedOptions = getResolvedOptions({
    field: attribute.attrInternalName,
    allDropdowns,
    customConfig,
  }) as DropdownOption[];

  return (
    <BulkUpdateSingleSelectInput
      id={attribute.attrInternalName}
      value={selectedValue}
      options={resolvedOptions}
      onChange={onChange}
      onBlur={onBlur}
      disabled={!state.enabled}
      hasError={state.error}
    />
  );
}

function renderNumberInput({ attribute, state, onChange, onBlur }: BulkInputRenderContext) {
  const minValue = typeof attribute.attrMinValue === 'number' ? attribute.attrMinValue : undefined;
  const maxValue = typeof attribute.attrMaxValue === 'number' ? attribute.attrMaxValue : undefined;

  return (
    <BulkUpdateNumberInput
      id={attribute.attrInternalName}
      value={typeof state.value === 'number' ? state.value : null}
      onChange={onChange}
      onBlur={onBlur}
      disabled={!state.enabled}
      hasError={state.error}
      min={minValue}
      max={maxValue}
    />
  );
}

function renderStringInput({ attribute, state, onChange, onBlur }: BulkInputRenderContext) {
  return (
    <BulkUpdateStringInput
      id={attribute.attrInternalName}
      value={typeof state.value === 'string' ? state.value : ''}
      onChange={onChange}
      onBlur={onBlur}
      disabled={!state.enabled}
      hasError={state.error}
    />
  );
}

function renderFallbackInput(attribute: BulkAttributeConfig) {
  return (
    <BulkUpdateStringInput
      id={attribute.attrInternalName}
      value=''
      onChange={() => undefined}
      disabled
      hasError
    />
  );
}

export default function BulkUpdateInputRenderer({
  attribute,
  state,
  onChange,
  onBlur,
}: BulkInputRendererProps) {
  const { customConfig, allDropdowns } = useColumnContextAPI();
  const type = resolveBulkDataType(attribute);
  const renderInputArgs: BulkInputRenderContext = {
    attribute,
    state,
    onChange,
    onBlur,
    customConfig,
    allDropdowns,
  };

  switch (type) {
    case 'date':
      return renderDateInput(renderInputArgs);
    case 'singleSelect':
      return renderSingleSelectInput(renderInputArgs);
    case 'number':
      return renderNumberInput(renderInputArgs);
    case 'string':
      return renderStringInput(renderInputArgs);
    case 'unknown':
      return renderFallbackInput(attribute);
  }
}
